import { Box, Button, Group, Stack, Table, Text } from "@mantine/core"
import { ShoppingCartSimpleIcon } from "@phosphor-icons/react"
import { useTranslation } from "react-i18next"
import { HelpIcon } from "@dehub/theme"
import { currencyFormat } from "../../../utils/currency"
import { DotPill, type Tone } from "../../delivery/atoms"
import { ListState } from "../../ui"
import { ProductThumb } from "../product-list/ProductThumb"
import { isAddable, isBad, lineAmount, type DisplayRow, type DisplayStatus } from "./match-rows"
import classes from "./quick-order.module.css"

const TONE: Record<DisplayStatus, Tone> = { MATCHED: "green", LOW_STOCK: "amber", NOT_FOUND: "red", INVALID: "red" }

type Props = {
  rows: DisplayRow[]
  state: "empty" | "loading" | "error" | "data"
  onRetry: () => void
  onRemoveBad: () => void
  onAdd: () => void
  /** The result is stale or the catalogue (warehouses) is still loading. */
  addDisabled?: boolean
}

/** Right card of OrdB_QuickOrder: «Таарсан байдал», one row per source line, totals and actions. */
export function MatchTable({ rows, state, onRetry, onRemoveBad, onAdd, addDisabled = false }: Readonly<Props>) {
  const { t } = useTranslation("bulkOrder")
  const matched = rows.filter((r) => r.status === "MATCHED" || r.status === "LOW_STOCK").length
  const bad = rows.filter(isBad).length
  const addable = rows.filter(isAddable)
  const total = addable.reduce((sum, r) => sum + lineAmount(r), 0)

  return (
    <Box className={classes.card}>
      <Box className={classes.cardHead}>
        <Text className={classes.cardTitle}>{t("match.title")}<HelpIcon helpKey="order.buyer-sales-quick-order.types.match-status" /></Text>
        {state === "data" && <Text className={classes.hint}>{t("match.summary", { total: rows.length, matched, bad })}</Text>}
      </Box>

      {state === "empty" && <ListState state="empty" title={t("match.emptyTitle")} description={t("match.emptyText")} />}
      {state === "loading" && <ListState state="loading" columns={6} rows={4} />}
      {state === "error" && (
        <ListState state="error" title={t("match.errorTitle")} description={t("match.errorText")} onRetry={onRetry} />
      )}

      {state === "data" && (
        <>
          <Table.ScrollContainer minWidth={720}>
            <Table verticalSpacing="sm" layout="fixed">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={56}>{t("match.col.no")}</Table.Th>
                  <Table.Th>{t("match.col.product")}</Table.Th>
                  <Table.Th w={64} ta="right">{t("match.col.qty")}</Table.Th>
                  <Table.Th w={104} ta="right">{t("match.col.price")}</Table.Th>
                  <Table.Th w={112} ta="right">{t("match.col.amount")}</Table.Th>
                  <Table.Th w={190}>{t("match.col.status")}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map((row, i) => (
                  <MatchRow key={row.key} index={i + 1} row={row} />
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
          <Box className={classes.cardFoot}>
            <Text className={classes.hint}>
              {t("match.total")}{" "}
              <Text span fw={700} className={classes.mono} c="var(--nw-text)">
                {currencyFormat(total)}
              </Text>
            </Text>
            <Group gap="sm">
              <Button variant="default" size="md" disabled={bad === 0} onClick={onRemoveBad}>
                {t("match.removeBad")}
              </Button>
              <Button size="md" leftSection={<ShoppingCartSimpleIcon size={18} />} disabled={addDisabled || addable.length === 0} onClick={onAdd}>
                {t("match.add", { count: addable.length })}
              </Button>
            </Group>
          </Box>
        </>
      )}
    </Box>
  )
}

function MatchRow({ index, row }: Readonly<{ index: number; row: DisplayRow }>) {
  const { t } = useTranslation("bulkOrder")
  const found = row.status === "MATCHED" || row.status === "LOW_STOCK"
  const hint =
    row.status === "LOW_STOCK"
      ? t("status.lowStockHint", { count: row.addQty })
      : row.status === "NOT_FOUND"
        ? t("status.notFoundHint")
        : row.status === "INVALID"
          ? t("status.invalidHint")
          : ""

  return (
    <Table.Tr>
      <Table.Td className={classes.mono}>{index}</Table.Td>
      <Table.Td>
        <Group gap="sm" wrap="nowrap" align="center">
          <Box className={`${classes.thumb} ${found ? "" : classes.thumbHidden}`}>
            <ProductThumb src={row.image} alt={row.name || ""} iconSize={18} />
          </Box>
          <Box miw={0}>
            <Text className={classes.mono} translate="no">
              {row.skuCode || row.code}
            </Text>
            <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)" lineClamp={2}>
              {row.status === "NOT_FOUND" ? t("match.notInCatalogue") : row.name || "-"}
            </Text>
          </Box>
        </Group>
      </Table.Td>
      <Table.Td ta="right" className={classes.mono}>
        {row.status === "INVALID" ? "-" : row.quantity}
      </Table.Td>
      <Table.Td ta="right" className={classes.mono}>
        {found && Number(row.price) > 0 ? currencyFormat(Number(row.price)) : "–"}
      </Table.Td>
      <Table.Td ta="right" className={classes.mono}>
        {found && row.addQty > 0 && Number(row.price) > 0 ? currencyFormat(lineAmount(row)) : "–"}
      </Table.Td>
      <Table.Td>
        <Stack gap={4} align="flex-start">
          <DotPill tone={TONE[row.status]}>{t(`status.${row.status}`)}</DotPill>
          {hint && <Text className={classes.hint}>{hint}</Text>}
        </Stack>
      </Table.Td>
    </Table.Tr>
  )
}
